import React, {ReactElement, ReactNode} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

import {cssTheme} from '../../../themes'
import minus from '../../../utils/minus'
import {Button} from '../Button'
import {Box} from '../helpers/Box'
import {Icon} from '../Icon'

export const ALERT_SIZES = ['compact', 'default'] as const
export type AlertSize = (typeof ALERT_SIZES)[number]

// Get variants from the 'default' size (assuming all sizes have same variants)
export const ALERT_VARIANTS = Object.keys(
  cssTheme.colors.alert.default
) as Array<keyof typeof cssTheme.colors.alert.default>
export type AlertVariant = (typeof ALERT_VARIANTS)[number]

interface Props {
  /** An icon placed before the alert content. It can be any type of React Element */
  icon?: ReactElement
  /** The content of the Alert, a React component or pure text */
  children: ReactNode
  /** Pass through classname to allow styles overrides */
  className?: string
  /** Title string of the Alert (hidden when size is "compact") */
  title?: string | ReactNode
  /** Passing this callback will show the close button.
   * Pressing the button will cause the callback execution.
   * Note: Ignored when size is "compact" */
  onClose?: () => void
  /** Color schemes are based on pre-defined themes */
  variant?: AlertVariant
  /** Size of the alert component. "compact" hides title and close button for minimal inline use */
  size?: AlertSize
  /** Identify the element for selection in integration tests, FullStory, etc. */
  dataId?: string
  /** Style prop is used by Linaria to pass CSS variables in cases when we need to style custom components in this way: `const StyledAlert = styled(Alert)` */
  style?: React.CSSProperties
}

const getVariantColor = (size: AlertSize, variant: AlertVariant) => `
  &.--size-${size}.--variant-${variant} {
    color: ${cssTheme.colors.alert[size][variant].content};
    & > a {
      color: ${cssTheme.colors.alert[size][variant].content}!important;
    }
  }
`

const getVariantStyles = (size: AlertSize, variant: AlertVariant) => `
  &.--size-${size}.--variant-${variant} {
    background-color: ${cssTheme.colors.alert[size][variant].background};
    border: 1px solid ${cssTheme.colors.alert[size][variant].border};
  }
`

export const AlertWrapper = styled.div`
  ${ALERT_SIZES.flatMap(size =>
    ALERT_VARIANTS.map(variant => getVariantStyles(size, variant))
  ).join('')}
  border-radius: ${cssTheme.layout.radius.md};
  position: relative;
  overflow: hidden;
  z-index: 0;
  padding: ${cssTheme.layout.spacing.s500} ${cssTheme.layout.spacing.s400};

  /* Compact size adjustments - add your custom styles here */
  &.--size-compact {
    padding: ${cssTheme.layout.spacing.s250};
  }
`

const Content = styled.div`
  ${ALERT_SIZES.flatMap(size =>
    ALERT_VARIANTS.map(variant => getVariantColor(size, variant))
  ).join('')}
  ${cssTheme.typography.bodyS};
  flex-grow: 1;
`

export const Title = styled.div`
  ${ALERT_SIZES.flatMap(size =>
    ALERT_VARIANTS.map(variant => getVariantColor(size, variant))
  ).join('')}
  ${cssTheme.typography.labelM};
`

export const Description = styled.div`
  padding: 0;
  &.--has-title-and-icon {
    padding-inline-start: calc(
      ${cssTheme.layout.spacing.s600} + ${cssTheme.layout.spacing.s200}
    );
  }
  ${ALERT_SIZES.flatMap(size =>
    ALERT_VARIANTS.map(variant => getVariantColor(size, variant))
  ).join('')}
  ${cssTheme.typography.bodyS};
`

export const IconWrapper = styled(Box)`
  ${ALERT_SIZES.flatMap(size =>
    ALERT_VARIANTS.map(variant => getVariantColor(size, variant))
  ).join('')}
  min-height: ${cssTheme.fonts.lineHeight.sm};
  &.--has-title {
    min-height: ${cssTheme.fonts.lineHeight.md};
  }
`
export const ButtonWrapper = styled.div`
  min-width: ${cssTheme.layout.spacing.s600};
  color: ${cssTheme.colors.alert.default.neutral.content};
  margin-inline-end: ${minus(cssTheme.layout.spacing.s250)};
`

export const Alert = ({
  icon,
  onClose,
  children,
  className,
  variant = 'neutral',
  size = 'default',
  title,
  dataId = '',
  style
}: Props) => {
  const handleCloseButtonClick = (e: React.SyntheticEvent) => {
    e.preventDefault()
    onClose?.()
  }

  const isCompact = size === 'compact'
  const hasTitle = Boolean(title) && !isCompact
  const hasIcon = Boolean(icon)
  const hasTitleAndIcon = hasTitle && hasIcon
  const showCloseButton = Boolean(onClose) && !isCompact

  return (
    <AlertWrapper
      className={cx(`--size-${size}`, `--variant-${variant}`, className)}
      data-id={dataId}
      style={style}
    >
      <Box display="flex" flexWrap="nowrap" gap={isCompact ? 's250' : 's400'}>
        {icon && (
          <IconWrapper
            paddingTop="s100"
            className={cx(
              `--size-${size}`,
              `--variant-${variant}`,
              hasTitle && '--has-title'
            )}
          >
            {icon}
          </IconWrapper>
        )}
        <Content className={cx(`--size-${size}`, `--variant-${variant}`)}>
          {hasTitle && (
            <Title
              data-id="AlertTitle"
              className={cx(`--size-${size}`, `--variant-${variant}`)}
            >
              {title}
            </Title>
          )}
          {!hasTitle && (
            <Description
              className={cx(`--size-${size}`, `--variant-${variant}`)}
            >
              {children}
            </Description>
          )}
        </Content>

        {showCloseButton && (
          <ButtonWrapper>
            <Button
              hasTouchArea
              variant="ghost"
              size="sm"
              iconStart={
                <Icon
                  name="Close"
                  size="sm"
                  colorPath={`alert.${size}.${variant}.content`}
                />
              }
              onClick={handleCloseButtonClick}
            />
          </ButtonWrapper>
        )}
      </Box>
      {hasTitle && (
        <Description
          className={cx(
            `--size-${size}`,
            `--variant-${variant}`,
            hasTitleAndIcon && '--has-title-and-icon'
          )}
        >
          {children}
        </Description>
      )}
    </AlertWrapper>
  )
}
